/**
 * CRM reminder notifications — Phase 1 + Phase 2
 * - Pre-reminder 15 min before
 * - Primary + follow-up (+30 min) + overdue (+60 min)
 * - Quiet hours 10 PM – 8 AM IST
 * - Server FCM handled by Backend cron
 */

import { acknowledgeCrmReminder } from "./crmReminderAck";
import { logCrmReminderClientEvent } from "./crmReminderAnalyticsClient";

const TZ = "Asia/Kolkata";
const QUIET_START = 22;
const QUIET_END = 8;
const PRE_REMINDER_MS = 15 * 60 * 1000;
const FOLLOWUP_MS = 30 * 60 * 1000;
const OVERDUE_MS = 60 * 60 * 1000;

const REMINDER_SUFFIXES = ["", "_pre", "_followup", "_overdue"];

const browserTimers = new Map();

export function isAndroidWebView() {
  return typeof window !== "undefined" && Boolean(window.BeentaraAndroid);
}

export function isCrmReminderAcknowledged(lead) {
  if (!lead?.extra2) return false;
  try {
    const parsed = JSON.parse(lead.extra2);
    return Boolean(parsed?.acknowledged);
  } catch {
    return false;
  }
}

/** Defer to next 8:00 AM IST if inside quiet window (10 PM – 8 AM). */
export function applyQuietHours(triggerMs) {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: TZ,
      hour: "numeric",
      hour12: false,
    }).format(new Date(triggerMs))
  );

  if (hour >= QUIET_START || hour < QUIET_END) {
    const dayFmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    let dayStr = dayFmt.format(new Date(triggerMs));
    if (hour >= QUIET_START) {
      dayStr = dayFmt.format(new Date(triggerMs + 86400000));
    }
    const adjusted = Date.parse(`${dayStr}T08:00:00+05:30`);
    return Number.isNaN(adjusted) ? triggerMs : adjusted;
  }
  return triggerMs;
}

function parseReminderDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const normalizedTime = String(timeStr).trim().slice(0, 5);
  const iso = `${String(dateStr).trim()}T${normalizedTime}:00+05:30`;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
}

export async function ensureCrmNotificationPermission() {
  if (isAndroidWebView()) {
    try {
      window.BeentaraAndroid.requestNotificationPermission?.();
      return window.BeentaraAndroid.hasNotificationPermission?.() === true;
    } catch {
      return false;
    }
  }
  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  return (await Notification.requestPermission()) === "granted";
}

function buildMessages(lead) {
  const leadName = lead.name || lead.business_name || lead.mobile || "Lead";
  const desc =
    lead.followup_description?.trim() ||
    `Follow up with ${leadName}${lead.mobile ? ` (${lead.mobile})` : ""}`;

  return {
    preTitle: `Upcoming: ${leadName}`,
    preBody: `${desc} — in 15 minutes`,
    title: `CRM Reminder: ${leadName}`,
    body: desc,
    followTitle: `Follow-up: ${leadName}`,
    followBody: `${desc} — reminder not yet opened`,
    overdueTitle: `Overdue: ${leadName}`,
    overdueBody: `${desc} — follow-up is overdue`,
  };
}

function showBrowserNotification(title, body, leadId, phase = "primary") {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

  logCrmReminderClientEvent({
    leadId,
    eventType: "delivered",
    channel: "browser",
    phase,
  });

  const options = {
    body,
    icon: "/logo192.png",
    tag: `crm-reminder-${leadId}`,
    renotify: true,
    data: { leadId, url: `/crm?leadId=${leadId}` },
  };

  try {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.ready.then((reg) => reg.showNotification(title, options));
    } else {
      const n = new Notification(title, options);
      n.onclick = () => {
        window.focus();
        window.location.href = `/crm?leadId=${leadId}`;
        acknowledgeCrmReminder(leadId);
      };
    }
  } catch {
    /* ignore */
  }
}

function scheduleBrowserTimer(key, title, body, leadId, triggerAtMs) {
  const delay = triggerAtMs - Date.now();
  if (delay <= 0 || delay > 2147483647) return;

  if (browserTimers.has(key)) clearTimeout(browserTimers.get(key));

  const timerId = setTimeout(() => {
    browserTimers.delete(key);
    const phase = key.endsWith("_pre")
      ? "pre"
      : key.endsWith("_followup")
        ? "followup"
        : key.endsWith("_overdue")
          ? "overdue"
          : "primary";
    showBrowserNotification(title, body, leadId, phase);
  }, delay);

  browserTimers.set(key, timerId);
}

function nativeSchedule(id, title, body, atEpochMs, leadId) {
  if (!window.BeentaraAndroid?.scheduleCrmReminder) return false;
  try {
    window.BeentaraAndroid.scheduleCrmReminder(
      JSON.stringify({ id, title, body, atEpochMs, leadId: String(leadId) })
    );
    return true;
  } catch {
    return false;
  }
}

function reminderTimerKeys(leadId) {
  return REMINDER_SUFFIXES.map((suffix) => `${leadId}${suffix}`);
}

export function cancelCrmReminder(leadId) {
  const keys = reminderTimerKeys(String(leadId));
  keys.forEach((key) => {
    if (browserTimers.has(key)) {
      clearTimeout(browserTimers.get(key));
      browserTimers.delete(key);
    }
    if (isAndroidWebView()) {
      window.BeentaraAndroid?.cancelCrmReminder?.(key);
    }
  });
}

export function scheduleCrmReminder(lead) {
  if (!lead?.id || !lead.remainder_date || !lead.remainder_time) return false;
  if (lead.archieve === true || lead.archieve === "true") return false;
  if (String(lead.extra || "").toLowerCase() === "converted") return false;
  if (isCrmReminderAcknowledged(lead)) return false;

  const rawDue = parseReminderDateTime(lead.remainder_date, lead.remainder_time);
  if (!rawDue) return false;

  const primaryAt = applyQuietHours(rawDue);
  const now = Date.now();
  const leadId = String(lead.id);

  const {
    preTitle,
    preBody,
    title,
    body,
    followTitle,
    followBody,
    overdueTitle,
    overdueBody,
  } = buildMessages(lead);

  const preAt = primaryAt - PRE_REMINDER_MS;
  const followAt = primaryAt + FOLLOWUP_MS;
  const overdueAt = primaryAt + OVERDUE_MS;

  const scheduleOne = (key, t, notifTitle, notifBody) => {
    if (t <= now) return;
    if (isAndroidWebView()) {
      nativeSchedule(key, notifTitle, notifBody, t, leadId);
    } else {
      scheduleBrowserTimer(key, notifTitle, notifBody, leadId, t);
    }
  };

  scheduleOne(`${leadId}_pre`, preAt, preTitle, preBody);
  scheduleOne(leadId, primaryAt, title, body);
  scheduleOne(`${leadId}_followup`, followAt, followTitle, followBody);
  scheduleOne(`${leadId}_overdue`, overdueAt, overdueTitle, overdueBody);

  if (isAndroidWebView()) {
    logCrmReminderClientEvent({
      leadId,
      eventType: "scheduled",
      channel: "native",
      phase: "all",
    });
  }

  return true;
}

export async function syncCrmRemindersFromLeads(leads = []) {
  if (!Array.isArray(leads)) return;
  await ensureCrmNotificationPermission();
  leads.forEach((lead) => {
    const hasReminder = lead.remainder_date && lead.remainder_time;
    const isArchived = lead.archieve === true || lead.archieve === "true";
    const isConverted = String(lead.extra || "").toLowerCase() === "converted";

    if (!hasReminder || isArchived || isConverted || isCrmReminderAcknowledged(lead)) {
      if (lead.id) cancelCrmReminder(lead.id);
      return;
    }
    cancelCrmReminder(lead.id);
    scheduleCrmReminder(lead);
  });
}

export function scheduleCrmReminderAfterSave(lead, payload) {
  cancelCrmReminder(lead.id);
  scheduleCrmReminder({
    ...lead,
    remainder_date: payload.remainder_date ?? lead.remainder_date,
    remainder_time: payload.remainder_time ?? lead.remainder_time,
    followup_description: payload.followup_description ?? lead.followup_description,
  });
}

export async function handleCrmReminderDeepLink(leadId) {
  if (!leadId) return null;
  const ok = await acknowledgeCrmReminder(leadId);
  if (!ok) return null;
  cancelCrmReminder(leadId);
  return leadId;
}

export async function handleCrmReminderSnooze(leadId, minutes = 60) {
  const { snoozeCrmReminder } = await import("./crmReminderAck");
  const result = await snoozeCrmReminder(leadId, minutes);
  if (!result) return null;

  cancelCrmReminder(leadId);
  scheduleCrmReminder({
    id: leadId,
    remainder_date: result.remainder_date,
    remainder_time: result.remainder_time,
  });
  return result;
}

export function setupBrowserNotificationClickHandler() {
  if (typeof window === "undefined") return;

  navigator.serviceWorker?.addEventListener("message", (event) => {
    if (event.data?.type === "crm_reminder_click" && event.data.leadId) {
      handleCrmReminderDeepLink(event.data.leadId);
    }
  });
}
